/* eslint-disable jsx-a11y/label-has-associated-control */
import { Plus } from '@saleshandy/icons';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { AccordionCard } from '../../../../../shared/accordion-card/accordioncard';
import StepWrapper from '../../../../../shared/stepwrapper';
import { SelectableCard } from '../../../type';
import AiSequenceModalFooter from '../ai-sequence-modal-footer/ai-sequence-modal-footer';
import {
  resetHasStepSkipped,
  setAiSeqChatMessage,
  setHasAiSeqStepSkipped,
} from '../../../../../sequence-slice';

interface GoalSelectionStepProps {
  handleModalClose: () => void;
  goals?: any[];
  saveCurrentStepDetails?: (payload: any) => void;
}

/**
 *
 * @prop goals - it will display the goals that were fetched from ai.
 * @prop saveCurrentStepDetails - Function to store current step details.
 *
 * @returns React Component
 */
const GoalSelectionStep: React.FC<GoalSelectionStepProps> = ({
  goals = [],
  saveCurrentStepDetails = () => {},
}) => {
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [items, setItems] = useState<SelectableCard[]>([]);
  const dispatch = useDispatch();

  const itemRefs = useRef<
    Record<number, React.MutableRefObject<() => boolean>>
  >({});

  const handleAddGoal = useCallback(() => {
    const newId = Date.now();
    itemRefs.current[newId] = { current: () => true };
    setItems((prev) => [
      ...prev,
      {
        id: newId,
        heading: '',
        description: '',
        isEditable: true,
        isNew: true,
        isError: false,
      },
    ]);
    setExpandedId(newId);
    setSelectedIds([newId]);
  }, []);

  const handleUpdate = useCallback(
    (id: number, heading: string, description: string) => {
      setItems((prev) =>
        prev?.map((item) =>
          item?.id === id
            ? { ...item, heading, description, isEditable: false }
            : item,
        ),
      );
    },
    [],
  );

  const handleDelete = useCallback((id: number) => {
    setItems((prev) => prev?.filter((item) => item?.id !== id));
    delete itemRefs.current[id];
    setExpandedId((prev) => (prev === id ? null : prev));
    setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
  }, []);

  const isDisabled = useMemo(() => items.some((item) => item.isNew), [items]);

  const selectedItemHasError = useMemo(() => {
    if (selectedIds?.length === 0) return false;
    const selected = items?.find((item) => selectedIds?.includes(item?.id));
    return selected?.isError ?? false;
  }, [selectedIds, items]);

  const handleGoalSelection = useCallback(() => {
    const selectedGoal = items.find((item) => item.id === selectedIds[0]);
    dispatch(setAiSeqChatMessage(selectedGoal.heading));
    if (selectedGoal) {
      const goal = {
        name: selectedGoal?.heading?.trim(),
        description: selectedGoal?.description?.trim(),
        ...(selectedGoal?.isNew && { isCustom: true }),
      };
      saveCurrentStepDetails({ goal });
    }
  }, [items, selectedIds, saveCurrentStepDetails]);

  const handleGoalSkip = useCallback(() => {
    dispatch(setHasAiSeqStepSkipped(true));
    saveCurrentStepDetails({ hasSkipped: true });
    dispatch(resetHasStepSkipped());
  }, [saveCurrentStepDetails]);

  useEffect(() => {
    setItems(
      goals?.map((goal, index) => ({
        id: index + 1,
        heading: goal?.name,
        description: goal?.description,
        isEditable: true,
        isNew: false,
      })) || [],
    );
  }, []);

  return (
    <StepWrapper heading="Choose your Primary Campaign Goal">
      {items.map((item) => {
        if (!itemRefs.current[item.id]) {
          itemRefs.current[item.id] = { current: () => true };
        }

        return (
          <AccordionCard
            key={item?.id}
            type="radio"
            selected={selectedIds?.includes(item.id)}
            onSelect={() => {
              setSelectedIds([item?.id]);
            }}
            heading={item?.heading}
            description={item?.description}
            isNew={item?.isNew}
            expanded={expandedId === item?.id}
            onExpand={() => {
              if (expandedId !== null && itemRefs.current[expandedId]) {
                itemRefs.current[expandedId].current?.();
              }
              setExpandedId((prev) => (prev === item.id ? null : item.id));
            }}
            onCollapse={() => setExpandedId(null)}
            onUpdate={(heading, description) =>
              handleUpdate(item?.id, heading, description)
            }
            onDelete={item?.isNew ? () => handleDelete(item?.id) : undefined}
            placeholderHeading="Name your primary goal"
            placeholderDescription="Describe your primary goal in a few words…"
            validateRef={itemRefs?.current[item?.id]}
            descriptionLabel="Goal Description"
            headingLabel="Goal Name"
            dummyHeading="Name your primary goal"
            dummyDescription="Describe your primary goal in a few words…"
            onValidationResult={(hasError) => {
              setItems((prev) =>
                prev.map((el) =>
                  el?.id === item?.id ? { ...el, isError: hasError } : el,
                ),
              );
            }}
          />
        );
      })}
      <div
        className={`add-feature ${isDisabled ? 'disabled' : ''}`}
        {...accessibleOnClick(isDisabled ? undefined : handleAddGoal)}
      >
        <Plus width={16} height={16} />
        <span>Add Goal</span>
      </div>

      <AiSequenceModalFooter
        showSkipBtn={true}
        onClickContinue={handleGoalSelection}
        disableContinueBtn={!!selectedItemHasError || selectedIds?.length === 0}
        onClickSkip={handleGoalSkip}
      />
    </StepWrapper>
  );
};

export default memo(GoalSelectionStep);
